import React, { useEffect, useRef } from 'react';
interface WireframeGlobeProps {
  size?: number;
  className?: string;
}
export function WireframeGlobe({
  size = 600,
  className = ''
}: WireframeGlobeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);
    let animationFrameId: number;
    let time = 0;
    // More points for a denser spiderweb mesh
    const numPoints = 350;
    const points: Array<{
      x: number;
      y: number;
      z: number;
      isHub: boolean;
      pulseOffset: number;
    }> = [];
    const phi = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < numPoints; i++) {
      const y = 1 - i / (numPoints - 1) * 2;
      const radius = Math.sqrt(1 - y * y);
      const theta = phi * i;
      const x = Math.cos(theta) * radius;
      const z = Math.sin(theta) * radius;
      points.push({
        x,
        y,
        z,
        isHub: Math.random() > 0.93,
        pulseOffset: Math.random() * Math.PI * 2
      });
    }
    // Background points for depth
    const numBgPoints = 60;
    const bgPoints: Array<{
      x: number;
      y: number;
      z: number;
    }> = [];
    for (let i = 0; i < numBgPoints; i++) {
      const y = 1 - i / (numBgPoints - 1) * 2;
      const radius = Math.sqrt(1 - y * y);
      const theta = phi * i * 1.5;
      const x = Math.cos(theta) * radius;
      const z = Math.sin(theta) * radius;
      bgPoints.push({
        x: x * 1.05,
        y: y * 1.05,
        z: z * 1.05
      });
    }
    // Minimal floating particles
    const numParticles = 10;
    const particles: Array<{
      speed: number;
      angle: number;
      radius: number;
      yOffset: number;
    }> = [];
    for (let i = 0; i < numParticles; i++) {
      particles.push({
        speed: Math.random() * 0.0015 + 0.0005,
        angle: Math.random() * Math.PI * 2,
        radius: 1.15 + Math.random() * 0.3,
        yOffset: (Math.random() - 0.5) * 1.5
      });
    }
    // Latitude and longitude lines for structure
    const latLines: Array<Array<{
      x: number;
      y: number;
      z: number;
    }>> = [];
    const lonLines: Array<Array<{
      x: number;
      y: number;
      z: number;
    }>> = [];
    const segments = 64;
    for (let lat = -60; lat <= 60; lat += 30) {
      const line = [];
      const rad = lat * Math.PI / 180;
      const y = Math.sin(rad);
      const r = Math.cos(rad);
      for (let i = 0; i <= segments; i++) {
        const theta = i / segments * Math.PI * 2;
        line.push({
          x: Math.cos(theta) * r,
          y,
          z: Math.sin(theta) * r
        });
      }
      latLines.push(line);
    }
    for (let lon = 0; lon < 180; lon += 45) {
      const line = [];
      const rad = lon * Math.PI / 180;
      for (let i = 0; i <= segments; i++) {
        const theta = i / segments * Math.PI * 2;
        line.push({
          x: Math.cos(rad) * Math.cos(theta),
          y: Math.sin(theta),
          z: Math.sin(rad) * Math.cos(theta)
        });
      }
      lonLines.push(line);
    }
    // Wave pulse state
    let waveCenter: {
      x: number;
      y: number;
      z: number;
    } | null = null;
    let waveRadius = 0;
    let waveActive = false;
    let lastWaveTime = 0;
    const render = () => {
      time += 0.0018;
      ctx.clearRect(0, 0, size, size);
      const cx = size / 2;
      const cy = size / 2;
      const globeRadius = size * 0.38;
      // Trigger wave pulse every ~4 seconds
      if (!waveActive && time - lastWaveTime > 0.0018 * 60 * 4) {
        const randomPoint = points[Math.floor(Math.random() * points.length)];
        waveCenter = {
          ...randomPoint
        };
        waveRadius = 0;
        waveActive = true;
        lastWaveTime = time;
      }
      if (waveActive) {
        waveRadius += 0.015;
        if (waveRadius > 2.5) {
          waveActive = false;
        }
      }
      // Pulsing ambient glow
      const pulseGlow = (Math.sin(time * 4) + 1) / 2;
      const ambientOpacity1 = 0.12 + pulseGlow * 0.04;
      // Ambient glow
      const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, globeRadius * 1.8);
      gradient.addColorStop(0, `rgba(16, 185, 129, ${ambientOpacity1})`);
      gradient.addColorStop(0.5, 'rgba(16, 185, 129, 0.04)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, size, size);
      // Inner sphere gradient for 3D depth
      const innerGradient = ctx.createRadialGradient(cx - globeRadius * 0.3, cy - globeRadius * 0.3, 0, cx, cy, globeRadius);
      innerGradient.addColorStop(0, 'rgba(16, 185, 129, 0.12)');
      innerGradient.addColorStop(0.5, 'rgba(16, 185, 129, 0.04)');
      innerGradient.addColorStop(0.85, 'rgba(0, 0, 0, 0.3)');
      innerGradient.addColorStop(1, 'rgba(0, 0, 0, 0.6)');
      ctx.beginPath();
      ctx.arc(cx, cy, globeRadius, 0, Math.PI * 2);
      ctx.fillStyle = innerGradient;
      ctx.fill();
      // Highlight spot
      const highlightGradient = ctx.createRadialGradient(cx - globeRadius * 0.35, cy - globeRadius * 0.35, 0, cx - globeRadius * 0.35, cy - globeRadius * 0.35, globeRadius * 0.4);
      highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.08)');
      highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.beginPath();
      ctx.arc(cx - globeRadius * 0.35, cy - globeRadius * 0.35, globeRadius * 0.4, 0, Math.PI * 2);
      ctx.fillStyle = highlightGradient;
      ctx.fill();
      // Globe Contour (main outline) - subtler
      ctx.beginPath();
      ctx.arc(cx, cy, globeRadius, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(16, 185, 129, 0.25)';
      ctx.lineWidth = 1.5;
      ctx.shadowBlur = 10;
      ctx.shadowColor = 'rgba(16, 185, 129, 0.2)';
      ctx.stroke();
      ctx.shadowBlur = 0;
      // Second glow contour
      ctx.beginPath();
      ctx.arc(cx, cy, globeRadius * 1.015, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(16, 185, 129, 0.08)';
      ctx.lineWidth = 1;
      ctx.stroke();
      // Project 3D to 2D
      const project = (p: {
        x: number;
        y: number;
        z: number;
      }) => {
        const rx = p.x * Math.cos(time) - p.z * Math.sin(time);
        const rz = p.x * Math.sin(time) + p.z * Math.cos(time);
        const tilt = 0.3;
        const ry = p.y * Math.cos(tilt) - rz * Math.sin(tilt);
        const rz2 = p.y * Math.sin(tilt) + rz * Math.cos(tilt);
        const scale = 2.5 / (2.5 + rz2);
        return {
          x: cx + rx * scale * globeRadius,
          y: cy + ry * scale * globeRadius,
          z: rz2,
          scale,
          origRotated: {
            x: rx,
            y: ry,
            z: rz2
          } // For wave distance calculation
        };
      };
      // Halo rings
      const haloTilt = 0.3;
      ctx.beginPath();
      ctx.ellipse(cx, cy, globeRadius * 1.15, globeRadius * 1.15 * Math.cos(haloTilt), 0, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(16, 185, 129, 0.15)';
      ctx.lineWidth = 1;
      ctx.stroke();
      // Latitude lines - very subtle
      ctx.lineWidth = 0.5;
      latLines.forEach(line => {
        ctx.beginPath();
        let started = false;
        line.forEach(p => {
          const proj = project(p);
          if (proj.z < 0) {
            if (!started) {
              ctx.moveTo(proj.x, proj.y);
              started = true;
            } else {
              ctx.lineTo(proj.x, proj.y);
            }
          } else {
            started = false;
          }
        });
        ctx.strokeStyle = 'rgba(16, 185, 129, 0.08)';
        ctx.stroke();
      });
      // Longitude lines - very subtle
      ctx.lineWidth = 0.5;
      lonLines.forEach(line => {
        ctx.beginPath();
        let started = false;
        line.forEach(p => {
          const proj = project(p);
          if (proj.z < 0) {
            if (!started) {
              ctx.moveTo(proj.x, proj.y);
              started = true;
            } else {
              ctx.lineTo(proj.x, proj.y);
            }
          } else {
            started = false;
          }
        });
        ctx.strokeStyle = 'rgba(16, 185, 129, 0.06)';
        ctx.stroke();
      });
      // Project all points
      const projected = points.map(p => ({
        ...project(p),
        orig: p
      }));
      // Background points (back half)
      const projectedBg = bgPoints.map(p => project(p));
      projectedBg.forEach(p => {
        if (p.z > 0) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 0.6 * p.scale, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(16, 185, 129, 0.08)';
          ctx.fill();
        }
      });
      // ===== SPIDERWEB CONNECTION LINES & MESH =====
      const threshold = 0.55; // Increased for denser web
      // First pass: Draw triangular mesh for close points
      for (let i = 0; i < projected.length; i++) {
        const p1 = projected[i];
        if (p1.z > 0.2) continue; // Skip back faces for mesh
        let connectedPoints = [];
        for (let j = i + 1; j < projected.length; j++) {
          const p2 = projected[j];
          if (p2.z > 0.2) continue;
          const dx = p1.orig.x - p2.orig.x;
          const dy = p1.orig.y - p2.orig.y;
          const dz = p1.orig.z - p2.orig.z;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (dist < threshold * 0.7) {
            // Tighter threshold for triangles
            connectedPoints.push(p2);
          }
        }
        // Draw triangles if we have enough connected points
        for (let k = 0; k < connectedPoints.length - 1; k++) {
          const p2 = connectedPoints[k];
          const p3 = connectedPoints[k + 1];
          // Check distance between p2 and p3 to form a valid triangle
          const dx = p2.orig.x - p3.orig.x;
          const dy = p2.orig.y - p3.orig.y;
          const dz = p2.orig.z - p3.orig.z;
          const dist23 = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (dist23 < threshold * 0.7) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.lineTo(p3.x, p3.y);
            ctx.closePath();
            ctx.fillStyle = 'rgba(16, 185, 129, 0.04)';
            ctx.fill();
          }
        }
      }
      // Second pass: Draw connection lines
      for (let i = 0; i < projected.length; i++) {
        const p1 = projected[i];
        // Calculate wave intensity for p1
        let waveIntensity1 = 0;
        if (waveActive && waveCenter) {
          const dx = p1.orig.x - waveCenter.x;
          const dy = p1.orig.y - waveCenter.y;
          const dz = p1.orig.z - waveCenter.z;
          const distToCenter = Math.sqrt(dx * dx + dy * dy + dz * dz);
          // Wave is a ring of thickness 0.4
          if (Math.abs(distToCenter - waveRadius) < 0.4) {
            waveIntensity1 = 1 - Math.abs(distToCenter - waveRadius) / 0.4;
          }
        }
        for (let j = i + 1; j < projected.length; j++) {
          const p2 = projected[j];
          const dx = p1.orig.x - p2.orig.x;
          const dy = p1.orig.y - p2.orig.y;
          const dz = p1.orig.z - p2.orig.z;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (dist < threshold) {
            const zAvg = (p1.z + p2.z) / 2;
            const isFront = zAvg < 0.2;
            // Calculate wave intensity for p2
            let waveIntensity2 = 0;
            if (waveActive && waveCenter) {
              const dx2 = p2.orig.x - waveCenter.x;
              const dy2 = p2.orig.y - waveCenter.y;
              const dz2 = p2.orig.z - waveCenter.z;
              const distToCenter2 = Math.sqrt(dx2 * dx2 + dy2 * dy2 + dz2 * dz2);
              if (Math.abs(distToCenter2 - waveRadius) < 0.4) {
                waveIntensity2 = 1 - Math.abs(distToCenter2 - waveRadius) / 0.4;
              }
            }
            const maxWaveIntensity = Math.max(waveIntensity1, waveIntensity2);
            // Higher opacity for visible web
            const baseOpacity = isFront ? (1 - dist / threshold) * 0.7 : (1 - dist / threshold) * 0.1;
            // Vary line width based on distance
            const lineWidth = isFront ? 0.3 + (1 - dist / threshold) * 0.9 : 0.2;
            // Pulsing data flow on hub connections
            const pulse = (Math.sin(time * 12 + p1.orig.pulseOffset) + 1) / 2;
            const isHubPulsing = (p1.orig.isHub || p2.orig.isHub) && pulse > 0.85 && isFront;
            const isWavePulsing = maxWaveIntensity > 0.2 && isFront;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            if (isWavePulsing) {
              ctx.strokeStyle = `rgba(167, 243, 208, ${Math.min(baseOpacity + maxWaveIntensity * 0.5, 1)})`;
              ctx.shadowBlur = 8 * maxWaveIntensity;
              ctx.shadowColor = '#6ee7b7';
              ctx.lineWidth = lineWidth * (1 + maxWaveIntensity);
            } else if (isHubPulsing) {
              ctx.strokeStyle = `rgba(52, 211, 153, ${Math.min(baseOpacity * 2.5, 1)})`;
              ctx.shadowBlur = 6;
              ctx.shadowColor = '#34d399';
              ctx.lineWidth = lineWidth * 1.5;
            } else {
              ctx.strokeStyle = `rgba(16, 185, 129, ${baseOpacity})`;
              ctx.shadowBlur = 0;
              ctx.lineWidth = lineWidth;
            }
            ctx.stroke();
          }
        }
      }
      ctx.shadowBlur = 0;
      // Draw points
      projected.forEach(p => {
        const isFront = p.z < 0;
        const opacity = isFront ? 0.9 : 0.15;
        const pointSize = (p.orig.isHub ? 2.5 : 1.0) * p.scale;
        ctx.beginPath();
        ctx.arc(p.x, p.y, pointSize, 0, Math.PI * 2);
        if (p.orig.isHub && isFront) {
          ctx.fillStyle = `rgba(52, 211, 153, ${opacity})`;
          ctx.shadowBlur = 15;
          ctx.shadowColor = '#10b981';
        } else {
          ctx.fillStyle = `rgba(16, 185, 129, ${opacity})`;
          ctx.shadowBlur = 0;
        }
        ctx.fill();
      });
      ctx.shadowBlur = 0;
      // Minimal floating particles
      particles.forEach(p => {
        p.angle += p.speed;
        const px = Math.cos(p.angle) * p.radius;
        const py = Math.sin(p.angle * 1.3) * 0.4 + p.yOffset * 0.1;
        const pz = Math.sin(p.angle) * p.radius;
        const proj = project({
          x: px,
          y: py,
          z: pz
        });
        const opacity = proj.z < 0 ? 0.5 : 0.1;
        ctx.beginPath();
        ctx.arc(proj.x, proj.y, 1.2 * proj.scale, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(52, 211, 153, ${opacity})`;
        ctx.shadowBlur = 4;
        ctx.shadowColor = '#10b981';
        ctx.fill();
      });
      ctx.shadowBlur = 0;
      animationFrameId = requestAnimationFrame(render);
    };
    render();
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [size]);
  return <div className={`relative flex items-center justify-center ${className}`} data-id="element-185">
      <canvas ref={canvasRef} style={{
      width: '100%',
      height: '100%',
      maxWidth: `${size}px`,
      maxHeight: `${size}px`
    }} className="pointer-events-none" data-id="element-186" />
    </div>;
}